import Select, {
  type FormatOptionLabelMeta,
  type MultiValue,
} from "react-select";
import type { AiIntent } from "../../types/ai-intent";

type AiIntentOption = {
  value: string;
  label: string;
  description: string;
  enabled: boolean;
};

type AiIntentsFieldProps = {
  intents: AiIntent[];
  value: string[];
  onChange: (intentSlugs: string[]) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const normalizeSlugs = (values: string[]): string[] => [
  ...new Set(values.map((value) => value.trim()).filter(Boolean)),
];

const formatOptionLabel = (
  option: AiIntentOption,
  meta: FormatOptionLabelMeta<AiIntentOption>,
) => (
  <div title={option.description || option.label} className="min-w-0">
    <span className="block truncate">
      {option.label} {!option.enabled && "(inativa)"}
    </span>
    {meta.context === "menu" && (
      <span className="mt-0.5 block whitespace-normal text-xs leading-snug text-slate-500">
        {option.value}
        {option.description ? ` — ${option.description}` : ""}
      </span>
    )}
  </div>
);

export function AiIntentsField({
  intents,
  value,
  onChange,
  isLoading = false,
  isDisabled = false,
}: AiIntentsFieldProps) {
  const options = intents.map((intent) => ({
    value: intent.slug,
    label: intent.name,
    description: intent.description,
    enabled: intent.enabled,
  }));
  const optionSlugs = new Set(options.map((option) => option.value));

  // Mantém associações legadas visíveis até que o usuário decida removê-las.
  normalizeSlugs(value).forEach((slug) => {
    if (!optionSlugs.has(slug)) {
      options.push({
        value: slug,
        label: slug,
        description: "Intent ainda não cadastrada.",
        enabled: false,
      });
    }
  });

  const selectedSlugs = normalizeSlugs(value);
  const selectedOptions = options.filter((option) =>
    selectedSlugs.includes(option.value),
  );

  const handleChange = (selected: MultiValue<AiIntentOption>) => {
    onChange(normalizeSlugs(selected.map((option) => option.value)));
  };

  return (
    <div>
      <label className="form-label">Intenções relacionadas</label>
      <Select<AiIntentOption, true>
        isMulti
        closeMenuOnSelect={false}
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
        isOptionDisabled={(option) => !option.enabled}
        isLoading={isLoading}
        isDisabled={isLoading || isDisabled}
        classNamePrefix="ai-intents-select"
        className="text-sm"
        placeholder="Selecione as intenções..."
        loadingMessage={() => "Carregando intenções..."}
        noOptionsMessage={() => "Nenhuma intenção cadastrada"}
      />
      <p className="mt-1 text-xs text-slate-500">
        Selecione as intenções cadastradas que devem direcionar para esta skill.
      </p>
    </div>
  );
}
